import {
  BarChart3,
  CalendarDays,
  CreditCard,
  FileText,
  FolderKanban,
  LayoutGrid,
  LogOut,
  Settings,
  Timer,
  Users,
  X,
} from "lucide-react"
import { NavLink, useLocation } from "react-router-dom"
import { useAuth } from "../auth/AuthProvider"
import { cx } from "../lib/cx"
import type { Role } from "../types"

const navItems = [
  { label: "Dashboard", icon: LayoutGrid, path: "/", end: true, roles: ["Admin"] as Role[] },
  { label: "Clients", icon: Users, path: "/clients", roles: ["Admin", "Staff"] as Role[] },
  { label: "Projects", icon: FolderKanban, path: "/projects", roles: ["Admin", "Staff"] as Role[] },
  { label: "Time", icon: Timer, path: "/time", roles: ["Admin", "Staff", "Contractor"] as Role[] },
  { label: "Billing", icon: CreditCard, path: "/billing", roles: ["Admin"] as Role[] },
  { label: "Reports", icon: BarChart3, path: "/reports", roles: ["Admin"] as Role[] },
  { label: "Tasks", icon: CalendarDays, path: "/tasks", roles: ["Admin", "Staff", "Contractor"] as Role[] },
  { label: "Sales", icon: FileText, path: "/sales", roles: ["Admin"] as Role[] },
  { label: "Settings", icon: Settings, path: "/settings", roles: ["Admin", "Staff", "Contractor"] as Role[] },
]

type MobileBottomNavProps = {
  isMenuOpen?: boolean
  onClose?: () => void
  onMenuToggle?: () => void
}

const MobileBottomNav = ({ isMenuOpen, onClose, onMenuToggle }: MobileBottomNavProps) => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const initials = user?.name
    ? user.name
        .split(" ")
        .map((part) => part[0])
        .join("")
    : "AD"

  const visibleNavItems = navItems.filter((item) =>
    user?.role ? item.roles.includes(user.role) : false,
  )

  return (
    <>
      {/* Bottom Navigation Bar (Always Visible) */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-line bg-white/95 backdrop-blur-sm lg:hidden">
        <div className="mx-auto max-w-md px-4 py-2">
          <div className="flex items-center justify-around">
            {visibleNavItems.slice(0, 5).map((item) => {
              const Icon = item.icon
              const isActive =
                (item.end && location.pathname === item.path) ||
                (!item.end && location.pathname.startsWith(item.path))

              return (
                <NavLink
                  key={item.label}
                  to={item.path}
                  end={item.end}
                  className={cx(
                    "flex flex-col items-center gap-1 rounded-xl px-3 py-2 transition-all duration-200 active:scale-95",
                    isActive
                      ? "text-moss"
                      : "text-stone hover:text-slate-700",
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </NavLink>
              )
            })}
            {/* More button */}
            <button
              type="button"
              onClick={onMenuToggle}
              className={cx(
                "flex flex-col items-center gap-1 rounded-xl px-3 py-2 transition-all duration-200 active:scale-95",
                isMenuOpen ? "text-moss" : "text-stone hover:text-slate-700",
              )}
            >
              <div
                className={cx(
                  "flex h-5 w-5 items-center justify-center rounded-full transition-colors",
                  isMenuOpen ? "bg-moss/20" : "bg-slate-200",
                )}
              >
                <span className="text-[10px] font-bold">+</span>
              </div>
              <span className="text-[10px] font-medium">More</span>
            </button>
          </div>
        </div>
      </div>

      {/* Menu Popup - Bottom Grid (Slides Up) */}
      {isMenuOpen !== undefined && (
        <div
          className={cx(
            "fixed bottom-0 left-0 right-0 z-50 max-h-[85vh] transform overflow-y-auto rounded-t-[32px] bg-white shadow-2xl transition-transform duration-300 ease-out lg:hidden",
            isMenuOpen ? "translate-y-0" : "translate-y-full",
          )}
        >
          {/* Header */}
          <div className="sticky top-0 flex items-center justify-between border-b border-line bg-white px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-moss text-white shadow-soft">
                <span className="text-sm font-semibold">BP</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{user?.name || "User"}</p>
                <p className="text-xs text-stone">{user?.email || ""}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-stone transition hover:bg-slate-200 active:scale-95"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Grid Menu */}
          <div className="grid grid-cols-4 gap-3 p-6">
            {visibleNavItems.map((item) => {
              const Icon = item.icon
              const isActive =
                item.end && location.pathname === item.path
                  ? true
                  : !item.end && location.pathname.startsWith(item.path)

              return (
                <NavLink
                  key={item.label}
                  to={item.path}
                  end={item.end}
                  onClick={onClose}
                  className={cx(
                    "group flex flex-col items-center gap-2 rounded-2xl p-4 transition-all duration-200",
                    isActive
                      ? "bg-moss text-white shadow-lg scale-105"
                      : "bg-slate-50 text-slate-700 hover:bg-slate-100 hover:scale-105 active:scale-95",
                  )}
                >
                  <Icon
                    className={cx(
                      "h-6 w-6 transition-transform",
                      isActive ? "scale-110" : "group-active:scale-90",
                    )}
                  />
                  <span className="text-xs font-medium">{item.label}</span>
                </NavLink>
              )
            })}
          </div>

          {/* Footer Actions */}
          <div className="border-t border-line bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-600">
                  {initials}
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-900">{user?.role || "User"}</p>
                  <p className="text-xs text-stone">{user?.title || ""}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  logout()
                  if (onClose) onClose()
                }}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-700 transition hover:bg-slate-200 active:scale-95"
                title="Sign out"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MobileBottomNav
